import React, { useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";
import { useHashAnchor } from '../../hooks/useHashAnchor'
import { gsap } from 'gsap';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./index.less";
import { adaptSize } from '../../utils/remAdapter'

// 屏幕一图片
import mouse from '../../assets/newVersion/mouse.svg';
// 第二屏图片
import areaIcon from '../../assets/newVersion/area-icon.svg';
import goshopIcon from '../../assets/newVersion/goshop-icon.svg';
import lineIcon from '../../assets/newVersion/line-icon.svg';
import echartsIcon from '../../assets/newVersion/echarts-icon.svg';
import dataIcon from '../../assets/newVersion/data-icon.svg';
import riskIcon from '../../assets/newVersion/risk-icon.svg';
import monitorIcon from '../../assets/newVersion/monitor-icon.svg';
import keyIcon from '../../assets/newVersion/key-icon.svg';
// 第三屏图片
import msg1 from '../../assets/newVersion/msg-1.avif';
// 第四屏图片
import fourthBottom1 from '../../assets/newVersion/fourth-bottom-1.avif';
import fourthBottom2 from '../../assets/newVersion/fourth-bottom-2.avif';
import fourthBottom3 from '../../assets/newVersion/fourth-bottom-3.avif';
import doudou from '../../assets/newVersion/doudou-new.svg'

gsap.registerPlugin(ScrollTrigger);

// ==========================================
// 动画配置区域 - 修改这里的值来调整第二屏动画速度
const SECOND_SCREEN_ANIMATION_DURATION = 3; // 单位：秒，控制第二屏所有入场动画的总基准时长
// ==========================================

// 第四屏轮播数据
const carouselData = [
  {
    id: 1,
    image: fourthBottom1,
    w: adaptSize(1037),
    h: adaptSize(581),
    quote: "数字驱动新能源充电站选址、运营",
  },
  {
    id: 2,
    w: adaptSize(1037),
    h: adaptSize(581),
    image: fourthBottom2,
    quote: "在大数据基础上实现宏观规划、中观布局、微观选址",
  },
  {
    id: 3,
    w: adaptSize(720),
    h: adaptSize(623),
    image: fourthBottom3,
    quote: "高德充电服务-e站通，充电桩实时动态信息展示，助力\"找好桩 好找桩\"",
  },
];

const AUTO_PLAY_INTERVAL = 5000; // 5秒自动轮播

const Enterprise: React.FC = () => {
  useHashAnchor();
  const containerRef = useRef<HTMLDivElement>(null);

  // 状态及引用管理
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const progressTweenRef = useRef<gsap.core.Tween | null>(null);
  const isAnimating = useRef(false);
  const isInitialized = useRef(false);
  const isHoveringRef = useRef(false);
  const goToSlideRef = useRef<(index: number) => void>(() => { });

  // 光影位置和 hover 状态记录
  const lightPositionsRef = useRef<{ [key: number]: { x: number, y: number } }>({});
  const isHoveringBoxRef = useRef<{ [key: number]: boolean }>({});

  // 初始位置常量（使用 adaptSize 适配）
  const LIGHT_INITIAL_X = 58;
  const LIGHT_INITIAL_Y = -114;
  const LIGHT_SIZE = 150; // 光影尺寸 301px 的一半

  const updateActiveIndex = (index: number) => {
    setActiveIndex(index);
    activeIndexRef.current = index;
  };

  // 鼠标移动处理 - 光影跟随并记录位置
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>, index: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // 记录当前位置和 hover 状态（记录原始像素位置）
    lightPositionsRef.current[index] = { x, y };
    isHoveringBoxRef.current[index] = true;

    // 关键修复：类型断言确保是 number
    const offset = adaptSize(LIGHT_SIZE) as number;

    gsap.to(`.slide-item-${index + 1} .guangying`, {
      left: x - offset,
      top: y - offset,
      duration: 0.2,
      ease: "power2.out"
    });
  }, []);

  // 鼠标移出 in-r-box - 回到初始位置
  const handleBoxMouseLeave = useCallback((index: number) => {
    isHoveringBoxRef.current[index] = false;
    gsap.to(`.slide-item-${index + 1} .guangying`, {
      left: adaptSize(LIGHT_INITIAL_X), // GSAP 接受 any，这里不需要断言
      top: adaptSize(LIGHT_INITIAL_Y),
      duration: 0.3,
      ease: "power2.out"
    });
  }, []);

  // 轮播容器鼠标悬停控制（暂停/恢复自动播放）
  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    if (progressTweenRef.current) {
      progressTweenRef.current.pause();
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    if (progressTweenRef.current) {
      progressTweenRef.current.play();
    }
  }, []);

  // 处理指示点点击切换
  const goToSlide = useCallback((index: number) => {
    if (isAnimating.current || index === activeIndexRef.current) return;
    isAnimating.current = true;

    // 清除上一个进度条动画
    if (progressTweenRef.current) {
      progressTweenRef.current.kill();
      progressTweenRef.current = null;
    }

    const prevIndex = activeIndexRef.current;

    // 停止所有指示点动画
    carouselData.forEach((_, i) => {
      gsap.killTweensOf(`.indicator-left-${i}`);
      gsap.killTweensOf(`.indicator-right-${i}`);
    });

    // 图片切换动画时间线
    const tl = gsap.timeline({
      onComplete: () => {
        updateActiveIndex(index);
        isAnimating.current = false;

        // 关键修复：切换动画完成后，如果鼠标正 hovering 新 slide 的 in-r-box
        if (isHoveringBoxRef.current[index]) {
          const pos = lightPositionsRef.current[index];
          if (pos) {
            const offset = adaptSize(LIGHT_SIZE) as number; // 类型断言
            gsap.set(`.slide-item-${index + 1} .guangying`, {
              left: pos.x - offset,
              top: pos.y - offset
            });
          }
        }
      }
    });

    tl.to(`.slide-item-${prevIndex + 1}`, {
      opacity: 0,
      duration: 0.6,
      ease: "power2.inOut"
    }, 0);

    tl.to(`.card-item-${prevIndex + 1}`, {
      opacity: 0,
      duration: 0.6,
      ease: "power2.inOut"
    }, 0);

    tl.fromTo(`.slide-item-${index + 1}`,
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.inOut" },
      0
    );

    tl.fromTo(`.card-item-${index + 1}`,
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.inOut" },
      0
    );

    // 前一个指示点恢复非激活状态
    gsap.set(`.indicator-left-${prevIndex}`, {
      height: 1,
      width: 56,
      backgroundColor: "rgba(255,255,255,0.12)",
      position: "relative",
      zIndex: 1,
      left: "auto",
      bottom: "auto"
    });
    gsap.set(`.indicator-right-${prevIndex}`, {
      height: 1,
      width: 0,
      opacity: 0
    });

    // 其他非当前指示点复位
    carouselData.forEach((_, i) => {
      if (i !== index && i !== prevIndex) {
        gsap.set(`.indicator-left-${i}`, {
          height: 1,
          width: 56,
          backgroundColor: "rgba(255,255,255,0.12)",
          position: "relative",
          zIndex: 1,
          left: "auto",
          bottom: "auto"
        });
        gsap.set(`.indicator-right-${i}`, {
          height: 1,
          width: 0,
          opacity: 0
        });
      }
    });

    // 新指示点激活动画
    gsap.set(`.indicator-right-${index}`, {
      height: 3,
      width: 104,
      opacity: 1,
      backgroundColor: "rgba(255,255,255,0.12)",
      position: "relative",
      zIndex: 1
    });

    gsap.set(`.indicator-left-${index}`, {
      height: 3,
      width: 0,
      backgroundColor: "#FFFFFF",
      position: "absolute",
      left: 0,
      bottom: 0,
      zIndex: 2
    });

    // 进度条动画
    progressTweenRef.current = gsap.to(`.indicator-left-${index}`, {
      width: 104,
      duration: AUTO_PLAY_INTERVAL / 1000,
      ease: "none",
      onComplete: () => {
        const nextIndex = (index + 1) % carouselData.length;
        goToSlideRef.current(nextIndex);
      }
    });

    // 如果正在 hover，暂停进度条
    if (isHoveringRef.current) {
      progressTweenRef.current.pause();
    }

  }, []);

  // 同步最新的跳转方法给 ref
  useEffect(() => {
    goToSlideRef.current = goToSlide;
  }, [goToSlide]);

  // 初始化指示点状态
  useLayoutEffect(() => {
    if (isInitialized.current) return;

    const timer = setTimeout(() => {
      carouselData.forEach((_, i) => {
        if (i === 0) {
          gsap.set(`.indicator-right-${i}`, {
            height: 3,
            width: 104,
            opacity: 1,
            backgroundColor: "rgba(255,255,255,0.12)",
            position: "relative",
            zIndex: 1
          });

          gsap.set(`.indicator-left-${i}`, {
            height: 3,
            width: 0,
            backgroundColor: "#FFFFFF",
            position: "absolute",
            left: 0,
            bottom: 0,
            zIndex: 2
          });

          progressTweenRef.current = gsap.to(`.indicator-left-${i}`, {
            width: 104,
            duration: AUTO_PLAY_INTERVAL / 1000,
            ease: "none",
            onComplete: () => {
              goToSlideRef.current(1);
            }
          });

        } else {
          gsap.set(`.indicator-left-${i}`, {
            height: 1,
            width: 56,
            backgroundColor: "rgba(255,255,255,0.12)",
            position: "relative",
            zIndex: 1
          });
          gsap.set(`.indicator-right-${i}`, {
            height: 1,
            width: 0,
            opacity: 0
          });
        }
      });

      isInitialized.current = true;
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      // 如果当前有激活的 slide，重置光影位置
      const currentIndex = activeIndexRef.current;
      if (!isHoveringBoxRef.current[currentIndex]) {
        gsap.set(`.slide-item-${currentIndex + 1} .guangying`, {
          left: adaptSize(LIGHT_INITIAL_X),
          top: adaptSize(LIGHT_INITIAL_Y),
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // GSAP 滚动动画初始化
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    const ctx = gsap.context(() => {
      ScrollTrigger.getAll().forEach(t => t.kill());

      // ========== 第一屏：简单的入场动画 ==========
      gsap.fromTo('.scroll-first .f-title',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          delay: 0.2
        }
      );

      gsap.fromTo('.scroll-first .f-msg',
        { opacity: 0, y: adaptSize(30) },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          delay: 0.5
        }
      );

      // ==========================================
      // ========== 第二屏：分步骤滚动动画 ==========
      // ==========================================
      const stage1Duration = SECOND_SCREEN_ANIMATION_DURATION * 0.35;
      const stage2Duration = SECOND_SCREEN_ANIMATION_DURATION * 0.65;
      const stage5Duration = SECOND_SCREEN_ANIMATION_DURATION * 0.6;

      const secondSection = gsap.timeline({
        scrollTrigger: {
          trigger: '.scroll-second',
          start: 'top 80%',
          toggleActions: "play none none none",
          once: true,
        }
      });

      // 左侧标题区域入场
      secondSection.fromTo('.ss-top .sst-left',
        { opacity: 0 },
        { opacity: 1, duration: stage1Duration, ease: "power2.out" }

      ).fromTo('.ss-top .sstr-item',
        { opacity: 0 },
        { opacity: 1, duration: stage2Duration * 0.2, ease: "power2.out" },
        '-=0.5'
      ).from('.sst-bg',
        { opacity: 0, duration: stage2Duration * 0.2, ease: "power2.in" }, '<'
      ).to('.ss-top .sstr-item .mask',
        { opacity: 0, duration: stage2Duration * 0.6, ease: "power2.inOut" }, '>'
      );

      secondSection.to('.ssc-bg-light', { opacity: 1, duration: 0.6 }, '>-0.7'
      ).to('.ss-center .sstr-item-2', { opacity: 1, duration: 0.3 }, '<'
      ).to(
        '.ss-center .sstr-item-2 .mask',
        { opacity: 0, duration: stage2Duration * 0.5, ease: "power2.inOut" }, '>'
      ).to(
        '.ssc-bg-grid', { opacity: 1, duration: 0.25 }, '<+0.25'
      ).to(
        '.ssc-top', { opacity: 1, duration: 0.25 }, '<'
      ).fromTo('.ssb-item',
        { opacity: 0, y: adaptSize(673) },
        {
          opacity: 1,
          y: 0,
          duration: stage5Duration,
          stagger: 0.3,
          ease: 'power2.out'
        }, '>-0.2'
      );

      // ========== 第三屏：左右分步进入 ==========
      gsap.fromTo('.scroll-third .stb-head',
        { opacity: 0, y: adaptSize(30) },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: '.scroll-third',
            start: 'top 75%',
            toggleActions: "play none none none",
            once: true,
          }
        }
      );

      gsap.fromTo('.stbb-left',
        { opacity: 0, x: adaptSize(-50) },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: '.stb-bottom',
            start: 'top 80%',
            toggleActions: "play none none none",
            once: true,
          }
        }
      );

      gsap.fromTo('.stbb-right',
        { opacity: 0, x: adaptSize(50) },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: '.stb-bottom',
            start: 'top 80%',
            toggleActions: "play none none none",
            once: true,
          }
        }
      );

      // 地图揭示动画
      const revealTl = gsap.timeline({
        scrollTrigger: {
          trigger: '.stb-bottom',
          start: 'top 60%',
          toggleActions: "play none none none",
          once: true,
        }
      });


      revealTl.fromTo('.map-clear-layer',
        { width: '0%' },  // 改为百分比
        {
          width: '50%',   // 明确设为 50%，随容器自适应
          duration: 3,
          ease: 'power2.inOut'
        }, 0
      );

      revealTl.fromTo('.map-divider',
        { left: '0%' },   // 改为百分比
        {
          left: '50%',    // 对应 50% 位置
          duration: 3,
          ease: 'power2.inOut'
        }, 0
      );

      revealTl.to('.stbbrt-item:nth-child(1)', {
        flex: '0 0 50%',
        paddingLeft: adaptSize(32),
        paddingRight: adaptSize(32),
        duration: 3,
        ease: 'power2.inOut'
      }, 0);

      revealTl.to('.stbbrt-item:nth-child(2)', {
        flex: '0 0 50%',
        duration: 3,
        ease: 'power2.inOut'
      }, 0);

      // ========== 第四屏：轮播初始化 ==========
      gsap.set('.slide-item', { opacity: 0 });
      gsap.set('.slide-item-1', { opacity: 1 });
      gsap.set('.card-item', { opacity: 0 });
      gsap.set('.card-item-1', { opacity: 1 });

    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="intelli-corp-page" ref={containerRef}>
      {/* 第一屏 */}
      <div className="scroll-first">
        <div className="f-title">助力企业·数智升级</div>
        <div className="f-msg">海量人地大数据 定制化开发企业服务，赋能决策</div>
        <img src={mouse} alt="" className="mouse" />
      </div>

      {/* 第二屏 */}
      <div className="scroll-second" id="qiyeditu">
        <div className="ss-top">
          <div className="sst-bg"></div>
          <div className="sst-left">
            <div className="ssl-title">企业地图</div>
            <div className="ssl-msg">解决企业与地图相关的问题</div>
          </div>
          <div className="sst-right">
            <div className="sstr-item">
              <img src={areaIcon} alt="" className="icon" />
              <span className="text">区域规划</span>
              <div className="mask"></div>
            </div>
            <div className="sstr-item">
              <img src={goshopIcon} alt="" className="icon" />
              <span className="text">网店管理</span>
              <div className="mask"></div>
            </div>
            <div className="sstr-item">
              <img src={lineIcon} alt="" className="icon" />
              <span className="text">路线规划</span>
              <div className="mask"></div>
            </div>
            <div className="sstr-item">
              <img src={echartsIcon} alt="" className="icon" />
              <span className="text">经营数据</span>
              <div className="mask"></div>
            </div>
          </div>
        </div>

        <div className="ss-center">
          <div className="ssc-bg-grid"></div>
          <div className="ssc-bg-light"></div>
          <div className="ssc-top"></div>
          <div className="sst-left"></div>
          <div className="sst-right">
            <div className="sstr-item-2">
              <img src={dataIcon} alt="" className="icon" />
              <span className="text">数据分析</span>
              <div className="mask"></div>
            </div>
            <div className="sstr-item-2">
              <img src={riskIcon} alt="" className="icon" />
              <span className="text">位置监控</span>
              <div className="mask"></div>
            </div>
            <div className="sstr-item-2">
              <img src={monitorIcon} alt="" className="icon" />
              <span className="text">风险预警</span>
              <div className="mask"></div>
            </div>
            <div className="sstr-item-2">
              <img src={keyIcon} alt="" className="icon" />
              <span className="text">权限管理</span>
              <div className="mask"></div>
            </div>
          </div>
        </div>

        <div className="ss-bottom">
          <div className="ssb-item">
            <div className="top-bg"></div>
            <div className="square"></div>
            <div className="ssbi-title">智能派单系统</div>
            <div className="ssbi-msg">适用企业：</div>
            <div className="ssbi-enterprise">
              <div className="ssbie-item">3C家电</div>
              <div className="ssbie-item">本地生活</div>
              <div className="ssbie-item">装备制造</div>
            </div>
            <div className="ssbi-msgs">
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">维修需求实时看板：可视化展示各省市待处理工单、紧急程度分级</div>
              </div>
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">智能派单引擎：根据工程师定位、技能标签、当前负荷自动分配最优工单</div>
              </div>
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">数据决策支持：维修耗时TOP分析、配件损耗统计等</div>
              </div>
            </div>
          </div>

          <div className="ssb-item">
            <div className="top-bg"></div>
            <div className="square"></div>
            <div className="ssbi-title">货车物流系统</div>
            <div className="ssbi-msg">适用企业：</div>
            <div className="ssbi-enterprise">
              <div className="ssbie-item">第三方物流公司</div>
              <div className="ssbie-item">冷链/危化品等特殊运输</div>
            </div>
            <div className="ssbi-msgs">
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">智能调度：根据货车装载情况、目的地等安排车辆调度</div>
              </div>
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">动态路径规划：实时避让拥堵路段，油耗降低15%以上</div>
              </div>
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">车辆偏离预设路线时自动触发告警</div>
              </div>
            </div>
          </div>

          <div className="ssb-item">
            <div className="top-bg"></div>
            <div className="square"></div>
            <div className="ssbi-title">经营大盘系统</div>
            <div className="ssbi-msg">适用企业：</div>
            <div className="ssbi-enterprise">
              <div className="ssbie-item">跨区域经营的连锁企业</div>
              <div className="ssbie-item">集团分子公司管理</div>
            </div>
            <div className="ssbi-msgs">
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">多维度数据融合：财务、人力、运营数据一键关联分析</div>
              </div>
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">预警雷达系统：关键指标异常自动标红</div>
              </div>
              <div className="ssbim-item">
                <div className="ssbimi-left"><div className="square"></div></div>
                <div className="ssbie-text">移动端适配：高管可随时通过手机查看实时经营热力图</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 第三屏 */}
      <div className="scroll-third" id="shineiditu">
        <div className="st-box">
          <div className="stb-head">
            <div className="stb-title">高标室内地图</div>
            <div className="stb-msg">高标室内地图</div>
          </div>
          <div className="stb-bottom">
            <img src={msg1} alt="" className="stbb-left" />
            <div className="stbb-right">
              <div className="stbbr-top">
                <div className="stbbrt-item">高标室内地图</div>
                <div className="stbbrt-item">普通地图</div>
              </div>
              <div className="stbbr-bottom">
                <div className="map-blur-layer"></div>
                <div className="map-clear-layer"></div>
                <div className="map-divider"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 第四屏 - 自动轮播 + 点击切换 + 光影跟随 */}
      <div
        id="xinnengyuanxuanzhi"
        className="scroll-fourth"
      >
        <div className="sf-bg"></div>
        <div className="sf-bg-logo"></div>

        <div className="sf-top">
          <div className="sft-title">新能源充电站选址、运营</div>
          <div className="sft-msg">
            <span className="sftm-item">科学选址</span>
            <span className="sftm-item">智慧运营</span>
          </div>
        </div>

        <div className="carousel-wrapper">
          {carouselData.map((item, index) => (
            <div
              key={item.id}
              className={`slide-item slide-item-${index + 1}`}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="box">
                <img
                  className="img"
                  src={item.image}
                  alt=""
                  style={{
                    width: `${item.w}px`,
                    height: `${item.h}px`,
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                />
                <div className={`right-box card-item card-item-${index + 1}`}>
                  <div className="in-box">
                    {/* 光影交互区域 */}
                    <div
                      className="in-r-box"
                      onMouseMove={(e) => handleMouseMove(e, index)}
                      onMouseLeave={() => handleBoxMouseLeave(index)}
                    >
                      <div className="guangying"></div>
                    </div>
                  </div>
                  <div className="rb-top">
                    <img
                      src={doudou}
                      style={{
                        width: `${adaptSize(19)}px`,
                        height: `${adaptSize(13)}px`,
                        position: 'absolute',
                        right: 0,
                        top: 0
                      }}
                      className="doudou1"
                      alt=""
                    />
                  </div>
                  <div className="rb-bottom">{item.quote}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 指示点 */}
        <div className="carousel-indicators">
          {carouselData.map((_, index) => (
            <div
              key={index}
              className={`indicator indicator-${index}`}
              onClick={() => goToSlide(index)}
            >
              <div className="indicator-hotzone"></div>
              <div className={`indicator-part indicator-left indicator-left-${index}`}></div>
              <div className={`indicator-part indicator-right indicator-right-${index}`}></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Enterprise;